(function () {
  const HOST_SELECTOR = 'atomic-commerce-facet';
  
  // Scroll Configuration
  const SCROLL_CONFIG = {
    enabled: true,
    behavior: 'smooth', // 'smooth' or 'auto'
    scrollDelay: 100, // delay in ms before scrolling
    allowedFacets: ['category', 'ec_brand', 'feature', 'price',"ec_promo_price", 'color', 'flavour', 'serving_size', 'weight_size', 'v_size', 'quantity', 'p_form', 'p_gender', 'gender',"v_flavour"], // Facets that trigger scroll on selection
  };

  // Collapse Prevention Configuration
  const COLLAPSE_CONFIG = {
    enabled: true,
    preventCollapseWithSelectedValues: true, // Prevent collapse if facet has selected values
  };

  // State management for tracking selections
  const state = {
    lastSelectedValues: new Map(), // Track selected values per facet
    facetSubscriptions: new Map(), // Track controller subscriptions
  };

  /** Utility: Debug logging with prefix */
  const debug = (...args) => {};

  /**
   * Smooth scroll to top of page
   */
  function smoothScrollToTop() {
    if (!SCROLL_CONFIG.enabled) return;
    
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: SCROLL_CONFIG.behavior
      });
    }, SCROLL_CONFIG.scrollDelay);
  }

  /**
   * Check if a facet has selected values
   * @param {HTMLElement} facet
   * @returns {boolean}
   */
  function facetHasSelectedValues(facet) {
    const controller = facet.facet;
    if (!controller?.state) return false;
    
    return controller.state.values?.some(v => v.state === 'selected') || false;
  }

  /**
   * Force expand a facet if it has selected values
   * @param {HTMLElement} facet
   */
  function forceExpandIfHasSelectedValues(facet) {
    if (!facetHasSelectedValues(facet)) return;

    const facetId = facet.facet?.state?.facetId;
    
    // Only expand if currently collapsed
    if (facet.isCollapsed || facet.hasAttribute('is-collapsed')) {
      debug(`Force expanding facet with selected values:`, facetId);
      
      // Remove attribute and set property
      facet.removeAttribute('is-collapsed');
      facet.isCollapsed = false;
    }
  }

  /**
   * Check if a facet selection has changed and trigger scroll if needed
   * @param {HTMLElement} facet
   */
  function checkForSelectionChange(facet) {
    const controller = facet.facet;
    if (!controller?.state) return false;

    const facetId = controller.state.facetId;
    
    // Debug logging
    debug('Checking selection for facet:', facetId);
    
    // Check if this facet is in the allowed list for scrolling
    if (!SCROLL_CONFIG.allowedFacets.includes(facetId.toLowerCase())) {
      debug(`Facet ${facetId} not in allowed list, skipping scroll check`);
      return false;
    }
    
    const currentSelectedValues = controller.state.values
      ?.filter(v => v.state === 'selected')
      .map(v => v.value)
      .sort() || [];

    const lastSelectedValues = state.lastSelectedValues.get(facetId) || [];
    
    debug('Selection values:', {
      facetId: facetId,
      current: currentSelectedValues,
      previous: lastSelectedValues
    });
    
    // Check if selection has changed
    const hasChanged = JSON.stringify(currentSelectedValues) !== JSON.stringify(lastSelectedValues);
    
    if (hasChanged) {
      state.lastSelectedValues.set(facetId, currentSelectedValues);
      
      // Only scroll if a new selection was added (not removed)
      if (currentSelectedValues.length > lastSelectedValues.length) {
        debug(`✓ Selection changed in ${facetId} - SCROLLING TO TOP`);
        smoothScrollToTop();
      } else {
        debug(`Selection changed in ${facetId} but count decreased (deselection) - not scrolling`);
      }
    }
    
    return hasChanged;
  }

  /**
   * Setup selection tracking and collapse prevention for a facet using only controller state
   * @param {HTMLElement} facet
   */
  function setupSelectionTracking(facet) {
    const controller = facet.facet;
    if (!controller?.state) {
      debug(`WARNING: No controller or state found for facet, will retry`);
      // Retry after a longer delay if controller isn't ready
      setTimeout(() => setupSelectionTracking(facet), 1000);
      return;
    }

    const facetId = controller.state.facetId;
    
    // Log the facet ID for debugging
    debug(`Setting up tracking for facet ID: "${facetId}" (lowercase: "${facetId.toLowerCase()}")`);
    debug(`Is in allowed list: ${SCROLL_CONFIG.allowedFacets.includes(facetId.toLowerCase())}`);
    
    // Initialize last selected values
    const currentSelectedValues = controller.state.values
      ?.filter(v => v.state === 'selected')
      .map(v => v.value)
      .sort() || [];
    state.lastSelectedValues.set(facetId, currentSelectedValues);
    
    debug(`Initialized selection tracking for: ${facetId} with ${currentSelectedValues.length} selected values`);
    
    // Force expand if already has selected values
    forceExpandIfHasSelectedValues(facet);
    
    // Clean up existing subscription if any
    const existingUnsubscribe = state.facetSubscriptions.get(facetId);
    if (existingUnsubscribe) {
      existingUnsubscribe();
    }
    
    // Subscribe to state changes if available
    if (controller.subscribe) {
      const unsubscribe = controller.subscribe(() => {
        debug(`State change detected for facet: ${facetId}`);
        
        // Handle scroll functionality
        checkForSelectionChange(facet);
        
        // Handle collapse prevention - check immediately and after a brief delay
        if (COLLAPSE_CONFIG.enabled && COLLAPSE_CONFIG.preventCollapseWithSelectedValues) {
          forceExpandIfHasSelectedValues(facet);
          
          // Double-check after a brief delay to catch any async collapse attempts
          setTimeout(() => {
            forceExpandIfHasSelectedValues(facet);
          }, 10);
        }
      });
      
      // Store the unsubscribe function for cleanup
      state.facetSubscriptions.set(facetId, unsubscribe);
      debug(`✓ Subscribed to state changes for: ${facetId}`);
    } else {
      debug(`WARNING: No subscribe method available for facet: ${facetId}`);
    }
  }

  /**
   * Apply style overrides to the target icon inside a facet's Shadow DOM.
   * @param {HTMLElement} facet - The facet component.
   * @returns {boolean} true if styles were applied, false otherwise.
   */
  function applyStylesToIcon(facet) {
    if (!facet?.shadowRoot) return false;
    const icon = facet.shadowRoot.querySelector('.left-0');
    if (icon) {
      icon.style.right = '0';
      icon.style.left = 'auto';
      debug('Styles applied to icon:', icon);
      return true;
    }
    return false;
  }

  /**
   * Observe the facet's Shadow DOM for icon appearance.
   * Disconnect after success or timeout.
   * @param {HTMLElement} facet
   */
  function observeShadowForIcon(facet) {
    if (!facet?.shadowRoot) return;
    const shadowObserver = new MutationObserver((mutations, obs) => {
      if (applyStylesToIcon(facet)) {
        obs.disconnect();
        debug('Shadow observer disconnected after success');
      }
    });
    shadowObserver.observe(facet.shadowRoot, { childList: true, subtree: true });
    // Stop after 5s to avoid leaks
    setTimeout(() => {
      shadowObserver.disconnect();
      debug('Shadow observer disconnected after timeout');
    }, 5000);
  }

  /**
   * Initialize a facet: Try immediately, observe shadow if needed, and setup selection tracking.
   * @param {HTMLElement} facet
   */
  function onFacetReady(facet) {
    // Apply icon styles
    if (!applyStylesToIcon(facet)) {
      // Observe shadow root if icon isn't ready
      observeShadowForIcon(facet);
    }

    // Setup selection tracking for scroll functionality and collapse prevention
    // Wait a bit to ensure facet controller is ready
    setTimeout(() => {
      setupSelectionTracking(facet);
    }, 500);
    
    // Also try again on click (facet might expand and render icon)
    facet.addEventListener('click', () => {
      requestAnimationFrame(() => {
        if (!applyStylesToIcon(facet)) {
          observeShadowForIcon(facet);
        }
        // Check for selection changes on click and force expand if needed
        setTimeout(() => {
          checkForSelectionChange(facet);
          forceExpandIfHasSelectedValues(facet);
        }, 200);
      });
    });
    
    debug('Facet initialized:', facet);
  }

  /**
   * Initialize all facets currently in DOM
   */
  function initExistingFacets() {
    const facets = document.querySelectorAll(HOST_SELECTOR);
    facets.forEach(onFacetReady);
    
    // Log all facet IDs after a delay to see what's available
    setTimeout(() => {
      const allFacetIds = Array.from(facets)
        .map(f => f.facet?.state?.facetId)
        .filter(Boolean);
      
      debug('=== ALL AVAILABLE FACET IDs ===');
      debug('Facet IDs found:', allFacetIds);
      debug('Allowed facets in config:', SCROLL_CONFIG.allowedFacets);
      
      // Check which allowed facets are missing
      const missingFacets = SCROLL_CONFIG.allowedFacets.filter(
        allowed => !allFacetIds.some(id => id.toLowerCase() === allowed.toLowerCase())
      );
      if (missingFacets.length > 0) {
        debug('⚠️ Facets in config but NOT found on page:', missingFacets);
      }
      
      // Check which facets won't trigger scroll
      const unmatchedFacets = allFacetIds.filter(
        id => !SCROLL_CONFIG.allowedFacets.includes(id.toLowerCase())
      );
      if (unmatchedFacets.length > 0) {
        debug('ℹ️ Facets on page but NOT in scroll config:', unmatchedFacets);
      }
      
      debug('=== END FACET DEBUG ===');
    }, 2000);
    
    debug(`Initialized ${facets.length} facets`);
  }

  /**
   * Observe DOM for new facets dynamically added
   */
  function observeNewFacets() {
    const hostObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.matches?.(HOST_SELECTOR)) {
            debug('New facet detected:', node);
            onFacetReady(node);
          }
        });
      });
    });
    hostObserver.observe(document.body, { childList: true, subtree: true });
    debug('Host observer started for dynamic facets');
  }

  // Initialize immediately on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExistingFacets);
  } else {
    initExistingFacets();
  }

  // Start observing for dynamically added facets
  observeNewFacets();

  // Cleanup function for page unload
  window.addEventListener('beforeunload', () => {
    // Unsubscribe from all controller subscriptions
    state.facetSubscriptions.forEach(unsubscribe => unsubscribe());
    state.facetSubscriptions.clear();
  });
})();